import dayjs from "dayjs";
import PropTypes from "prop-types";

import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

import { formatDate, formatDateTime, formatMoney, formatNumber } from "@/features/shared";
import { ReturnableTypeEnum, ReturnableTypeLabel } from "./returnable-type";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
    marginTop: 16,
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#ececec",
    fontFamily: "Helvetica-Bold",
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tableRow: {
    flexDirection: "row",
    borderColor: "#ececec",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  cell: {
    padding: 4,
  },
  attributeHeader: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginTop: 8,
  },
  infoSection: {
    flexDirection: "row",
    marginVertical: 16,
  },
  infoItem: {
    flex: 1,
  },
  summarySection: {
    marginTop: 16,
    alignItems: "flex-end",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    minWidth: 200,
  },
});

export const PurchaseReturnPdf = ({ purchaseReturn, user }) => {
  // Check if this is a Non-Faktur return (empty PI with PI-PRT prefix)
  const isNonInvoiceReturn =
    purchaseReturn.returnableType === ReturnableTypeEnum.PURCHASE_INVOICE &&
    purchaseReturn.returnable?.referenceNumber?.startsWith("PI-PRT");

  const displayReturnableType = isNonInvoiceReturn
    ? ReturnableTypeEnum.NON_INVOICE
    : purchaseReturn.returnableType;

  const showPricing =
    purchaseReturn.returnableType === ReturnableTypeEnum.PURCHASE_INVOICE ||
    purchaseReturn.returnableType === ReturnableTypeEnum.NON_INVOICE;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <View>
            <Text style={styles.header}>Retur Pembelian</Text>
            <Text
              style={{
                fontSize: 12,
                color: "#364F6B",
                fontFamily: "Helvetica-Bold",
                marginTop: 4,
              }}
            >
              {purchaseReturn.referenceNumber}
            </Text>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                textAlign: "right",
              }}
            >
              Dicetak oleh: {user.name}
            </Text>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                marginTop: 2,
                textAlign: "right",
              }}
            >
              Tanggal cetak: {formatDateTime(dayjs())}
            </Text>
          </View>
        </View>

        {/* Information Section */}
        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>Tanggal Transaksi</Text>
            <Text style={styles.attributeHeader}>
              {formatDate(dayjs(purchaseReturn.transactionDate))}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Pemasok</Text>
            <Text style={styles.attributeHeader}>
              {purchaseReturn.seller?.name || "-"}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Outlet</Text>
            <Text style={styles.attributeHeader}>
              {purchaseReturn.buyer?.name || "-"}
            </Text>
          </View>
        </View>

        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>Jenis Retur</Text>
            <Text style={styles.attributeHeader}>
              {displayReturnableType
                ? ReturnableTypeLabel[displayReturnableType]
                : "Non Faktur"}
            </Text>
          </View>
          {!isNonInvoiceReturn && purchaseReturn.returnableType && (
            <View style={styles.infoItem}>
              <Text>Return Dari</Text>
              <Text style={styles.attributeHeader}>
                {purchaseReturn.returnable?.referenceNumber || "-"}
              </Text>
            </View>
          )}
          <View style={styles.infoItem}>
            <Text>Catatan</Text>
            <Text style={styles.attributeHeader}>
              {purchaseReturn.note ?? "-"}
            </Text>
          </View>
        </View>

        {/* Products Table */}
        {purchaseReturn.purchaseReturnProductVariants?.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Return Produk Varian</Text>
            <View style={styles.tableHeader}>
              <Text style={[styles.cell, { flex: 4 }]}>Nama</Text>
              <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
                Qty
              </Text>
              <Text style={[styles.cell, { flex: 1 }]}>Satuan</Text>
              {showPricing && (
                <>
                  <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
                    Harga
                  </Text>
                  <Text style={[styles.cell, { flex: 1.5, textAlign: "right" }]}>
                    Total
                  </Text>
                </>
              )}
              <Text style={[styles.cell, { flex: 1.5 }]}>Catatan</Text>
            </View>
          </>
        )}
        {purchaseReturn.purchaseReturnProductVariants?.map((variant) => {
          const originalTotal = variant.quantity * (variant.price || 0);
          const finalTotal = originalTotal - (variant.discount || 0);
          const hasDiscount = variant.discount && variant.discount > 0;

          return (
            <View style={styles.tableRow} key={variant.id}>
              <View style={[styles.cell, { flex: 4 }]}>
                <Text>{variant.name}</Text>
                <Text style={{ color: "#666", fontSize: 8, marginTop: 2 }}>
                  {variant.sku}
                </Text>
              </View>
              <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
                {variant.quantity}
              </Text>
              <Text style={[styles.cell, { flex: 1 }]}>
                {variant.product.unitOfMeasurement.name}
              </Text>
              {showPricing && (
                <>
                  <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
                    {formatNumber(variant.price)}
                  </Text>
                  {!hasDiscount ? (
                    <Text style={[styles.cell, { flex: 1.5, textAlign: "right", fontFamily: "Helvetica-Bold" }]}>
                      {formatNumber(finalTotal)}
                    </Text>
                  ) : (
                    <View style={[styles.cell, { flex: 1.5, alignItems: "flex-end" }]}>
                      <Text style={{ fontFamily: "Helvetica-Bold" }}>
                        {formatNumber(finalTotal)}
                      </Text>
                      <Text
                        style={{
                          fontSize: 7,
                          color: "#666",
                          textDecoration: "line-through",
                          marginTop: 1,
                        }}
                      >
                        {formatNumber(originalTotal)}
                      </Text>
                    </View>
                  )}
                </>
              )}
              <Text style={[styles.cell, { flex: 1.5 }]}>
                {variant.note || "-"}
              </Text>
            </View>
          );
        })}

        {/* Summary Section - Only show for invoice/non-invoice returns */}
        {showPricing && (
          <View style={styles.summarySection}>
            <View style={styles.summaryRow}>
              <Text>Subtotal:</Text>
              <Text style={{ fontFamily: "Helvetica-Bold" }}>
                {formatMoney(purchaseReturn.subtotal)}
              </Text>
            </View>
            {(purchaseReturn.totalDiscount ?? purchaseReturn.discount) > 0 && (
              <>
                <View style={styles.summaryRow}>
                  <Text>Diskon:</Text>
                  <Text>
                    {purchaseReturn.discountType === "PERCENTAGE"
                      ? `${purchaseReturn.discountAmount ?? 0}% (~${formatMoney(
                          ((purchaseReturn.subtotal ?? 0) *
                            (purchaseReturn.discountAmount ?? 0)) /
                            100
                        )})`
                      : formatMoney(purchaseReturn.discountAmount ?? 0)}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text>Total Diskon:</Text>
                  <Text>
                    {formatMoney(
                      purchaseReturn.totalDiscount ?? purchaseReturn.discount
                    )}
                  </Text>
                </View>
              </>
            )}
            {purchaseReturn.taxable && (
              <View style={styles.summaryRow}>
                <Text>Pajak ({purchaseReturn.taxPercentage}%):</Text>
                <Text>{formatMoney(purchaseReturn.taxAmount)}</Text>
              </View>
            )}
            <View style={styles.summaryRow}>
              <Text style={{ fontFamily: "Helvetica-Bold" }}>Grand Total:</Text>
              <Text style={{ fontFamily: "Helvetica-Bold" }}>
                {formatMoney(purchaseReturn.grandTotal)}
              </Text>
            </View>
          </View>
        )}
      </Page>
    </Document>
  );
};

PurchaseReturnPdf.propTypes = {
  purchaseReturn: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};
